import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Save, RefreshCw, Settings, Server, Users, MessageCircle, BarChart3, Mail, MessageSquare, Target, TrendingUp, Code, Bell, Image, Database, Bot } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';

import { useCustomization } from '@/contexts/CustomizationContext';
import { NotificationTest } from '@/components/NotificationTest';
import { ExternalApiConfigTab } from '@/components/Config/ExternalApiConfigTab';
import { RiqueAtendimentoPromptTab } from '@/components/Config/RiqueAtendimentoPromptTab';

interface Config {
  key: string;
  value: string;
  description?: string;
  updatedAt: string;
}

interface SystemInfo {
  uptime: string;
  memory: {
    used: string;
    total: string;
    percentage: number;
  };
  database: {
    status: string;
    connections: number;
  };
  version: string;
  environment: string;
}

interface WhatsAppConfig {
  id?: string;
  provider: string;
  phoneNumber: string;
  accountSid?: string;
  authToken?: string;
  webhookUrl?: string;
  isActive: boolean;
  allowedUsers: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface WhatsAppStats {
  totalMessages: number;
  sentToday: number;
  successRate: number;
  lastMessage: string;
}

interface SystemCustomization {
  id?: string;
  companyId?: string;
  logoUrl?: string;
  systemName: string;
  createdAt?: string;
  updatedAt?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

export const ConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { customization: contextCustomization, applyCustomization, loadCustomization } = useCustomization();
  // Estados gerais
  const [activeTab, setActiveTab] = useState('geral');
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [configs, setConfigs] = useState<Config[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<Config | null>(null);
  const [configValue, setConfigValue] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Estados para Personalização
  const [customization, setCustomization] = useState<SystemCustomization>(contextCustomization);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [savingCustomization, setSavingCustomization] = useState(false);

  // Sincronizar com o contexto quando ele mudar
  useEffect(() => {
    setCustomization(contextCustomization);
    if (contextCustomization.logoUrl) {
      setLogoPreview(contextCustomization.logoUrl);
    }
  }, [contextCustomization]);



  // Estados para WhatsApp
  const [config, setConfig] = useState<WhatsAppConfig>({
    provider: 'twilio',
    phoneNumber: '',
    accountSid: '',
    authToken: '',
    webhookUrl: '',
    isActive: false,
    allowedUsers: []
  });
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<WhatsAppStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  // Estados para Email Templates
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailCategories, setEmailCategories] = useState<string[]>([]);
  const [emailCategoryDetails, setEmailCategoryDetails] = useState<any[]>([]);
  const [emailDefaultVariables, setEmailDefaultVariables] = useState<string[]>([]);
  const [emailFilters, setEmailFilters] = useState({ search: '', category: '' });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [savingEmailTemplate, setSavingEmailTemplate] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [testingEmailTemplate, setTestingEmailTemplate] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [emailForm, setEmailForm] = useState({
    name: '',
    subject: '',
    content: '',
    category: '',
    variables: [] as string[]
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });

  // Estados para Integração WhatsApp
  const [integrationConfig, setIntegrationConfig] = useState({
    baseUrl: '',
    apiId: '',
    apiToken: '',
    webhookUrl: '',
    isActive: false
  });
  const [integrationLoading, setIntegrationLoading] = useState(false);

  // Estados para WhatsApp Automação
  interface WhatsAppAutomation {
    id: string;
    name: string;
    description?: string;
    triggerType: string;
    triggerValue: number;
    templateId?: string | null;
    isActive: boolean;
    conditions: any[];
    segments: any[];
    schedule: any;
    retryConfig: { maxRetries: number; retryDelay: number };
    throttling: { maxMessagesPerHour: number; maxMessagesPerDay: number };
    createdAt: string;
    companyId?: string | null;
    _count?: { executions: number };
  }
  const [waAutomations, setWaAutomations] = useState<WhatsAppAutomation[]>([]);
  const [waShowModal, setWaShowModal] = useState(false);
  const [waSelected, setWaSelected] = useState<WhatsAppAutomation | null>(null);
  const [waForm, setWaForm] = useState({
    name: '',
    description: '',
    triggerType: '',
    triggerValue: 0,
    templateId: '',
    isActive: true,
    conditions: [] as any[],
    segments: [] as any[],
    schedule: { type: 'immediate' as const },
    retryConfig: { maxRetries: 3, retryDelay: 5 },
    throttling: { maxMessagesPerHour: 1000, maxMessagesPerDay: 10000 },
    companyId: '' as string | undefined,
  });
  const [waCompanies, setWaCompanies] = useState<any[]>([]);
  const [waSelectedCompany, setWaSelectedCompany] = useState<string>('');
  const [waTemplates, setWaTemplates] = useState<any[]>([]);
  const [waExecModal, setWaExecModal] = useState(false);
  const [waExecutions, setWaExecutions] = useState<any[]>([]);
  const [waStatsModal, setWaStatsModal] = useState(false);
  const [waStats, setWaStats] = useState<any | null>(null);
  const [waPreviewVars, setWaPreviewVars] = useState<Record<string, string>>({});

  const [waTemplateModal, setWaTemplateModal] = useState(false);
  const [waTemplateSelected, setWaTemplateSelected] = useState<any | null>(null);
  const [waTemplateForm, setWaTemplateForm] = useState({
    name: '',
    category: '',
    content: '',
    variables: [] as string[],
    isActive: true,
  });
  const [waTemplateSaving, setWaTemplateSaving] = useState(false);
  const [waTemplateTestPhone, setWaTemplateTestPhone] = useState('');

  // Estados para Limpeza de Dados
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  // Estados para Metas dos Operadores
  const [operadorMetas, setOperadorMetas] = useState<any[]>([]);
  const [operadores, setOperadores] = useState<any[]>([]);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState<any | null>(null);
  const [metaForm, setMetaForm] = useState({
    userId: '',
    tipo: 'DIARIA' as 'DIARIA' | 'SEMANAL' | 'MENSAL',
    titulos: 0,
    valorRecuperado: 0,
    taxaConversao: 0,
    tempoMedioResolucao: 0,
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupConfirmation, setCleanupConfirmation] = useState('');

  // Carregar dados iniciais
  useEffect(() => {
    loadSystemInfo();
    loadConfigs();
  }, []);

  // Carregar dados de WhatsApp quando a aba for ativada
  // Carregar personalização apenas uma vez quando a aba for ativada
  const [personalizationLoaded, setPersonalizationLoaded] = useState(false);
  
  useEffect(() => {
    if (activeTab === 'personalizacao' && !personalizationLoaded) {
      loadCustomization();
      setPersonalizationLoaded(true);
    }
  }, [activeTab, personalizationLoaded]);

  useEffect(() => {
    if (activeTab === 'whatsapp') {
      loadWhatsAppConfig();
      loadUsers();
      loadStats();
    }
  }, [activeTab]);

  // Carregar dados de email templates quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'emails') {
      loadEmailTemplates();
      loadEmailCategories();
      loadEmailCategoryDetails();
      loadEmailDefaultVariables();
    }
  }, [activeTab]);

  // Carregar dados de integração WhatsApp quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'whatsapp-integration') {
      loadIntegrationConfig();
    }
  }, [activeTab]);

  // Carregar dados de automação WhatsApp quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'whatsapp-automation') {
      loadWaCompanies();
      loadWaTemplates();
      loadWaAutomations();
    }
  }, [activeTab]);

  // Carregar dados de limpeza quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'data-cleanup') {
      loadCompanies();
    }
  }, [activeTab]);

  // Carregar dados de metas dos operadores quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'operador-metas') {
      loadOperadorMetas();
      loadOperadores();
    }
  }, [activeTab]);

  // Funções para sistema
  const loadSystemInfo = async () => {
    try {
      const response = await api.get('/config/system-info');
      setSystemInfo(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar informações do sistema:', error);
    }
  };

  const loadConfigs = async () => {
    try {
      const response = await api.get('/config');
      setConfigs(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  // Funções para Metas dos Operadores
  const loadOperadorMetas = async () => {
    try {
      const response = await api.get('/operador-meta/metas');
      setOperadorMetas(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar metas dos operadores');
    }
  };

  const loadOperadores = async () => {
    try {
      const response = await api.get('/users?role=OPERADOR&isActive=true');
      setOperadores(response.data.data.users || []);
    } catch (error) {
      toast.error('Erro ao carregar operadores');
    }
  };

  const handleSaveMeta = async () => {
    try {
      setSavingMeta(true);
      
      if (selectedMeta) {
        // Atualizar meta existente
        await api.put(`/operador-meta/metas/${selectedMeta.id}`, metaForm);
        toast.success('Meta atualizada com sucesso!');
      } else {
        // Criar nova meta
        await api.post('/operador-meta/metas', metaForm);
        toast.success('Meta criada com sucesso!');
      }
      
      setShowMetaModal(false);
      setSelectedMeta(null);
      setMetaForm({
        userId: '',
        tipo: 'DIARIA',
        titulos: 0,
        valorRecuperado: 0,
        taxaConversao: 0,
        tempoMedioResolucao: 0,
      });
      
      await loadOperadorMetas();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar meta');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDeleteMeta = async (metaId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta meta?')) {
      return;
    }
    
    try {
      await api.delete(`/operador-meta/metas/${metaId}`);
      toast.success('Meta excluída com sucesso!');
      await loadOperadorMetas();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir meta');
    }
  };

  const updateConfig = async () => {
    if (!selectedConfig) return;

    try {
      setSavingConfig(true);
      await api.put(`/config/${selectedConfig.key}`, {
        value: configValue
      });
      
      await loadConfigs();
      setShowConfigModal(false);
      toast.success('Configuração atualizada com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  // Funções para WhatsApp
  const loadWhatsAppConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/config/whatsapp');
      if (response.data.success && response.data.data) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.log('Configuração WhatsApp não encontrada, usando padrão');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data.users || []);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/whatsapp/stats');
      setStats(response.data.data);
    } catch (error) {
      console.log('Estatísticas WhatsApp não disponíveis');
    }
  };

  const validateConfig = () => {
    const newErrors: Record<string, string> = {};
    
    if (!config.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Número de telefone é obrigatório';
    } else if (!/^\+[1-9]\d{1,14}$/.test(config.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Formato inválido. Use: +5511999999999';
    }
    
    if (config.provider === 'twilio' && !config.accountSid?.trim()) {
      newErrors.accountSid = 'Account SID é obrigatório para Twilio';
    }
    
    if (!config.authToken?.trim()) {
      newErrors.authToken = 'Auth Token é obrigatório';
    }
    
    if (config.isActive && !config.webhookUrl?.trim()) {
      newErrors.webhookUrl = 'Webhook URL é obrigatório quando ativo';
    } else if (config.webhookUrl?.trim() && !/^https:\/\/.+/.test(config.webhookUrl.trim())) {
      newErrors.webhookUrl = 'Webhook deve usar HTTPS';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateConfig()) {
      toast.error('Por favor, corrija os erros antes de salvar');
      return;
    }
    
    try {
      setLoading(true);
      await api.post('/config/whatsapp', config);
      toast.success('Configuração WhatsApp salva com sucesso!');
      setErrors({});
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      await api.post('/config/whatsapp/test');
      toast.success('Conexão testada com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao testar conexão');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId: string) => {
    setConfig(prev => ({
      ...prev,
      allowedUsers: prev.allowedUsers.includes(userId)
        ? prev.allowedUsers.filter(id => id !== userId)
        : [...prev.allowedUsers, userId]
    }));
  };

  // Funções para Email Templates
  const loadEmailTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      const templates = response.data.data;
      setEmailTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Erro ao carregar templates de email:', error);
      setEmailTemplates([]);
    }
  };

  const loadEmailCategories = async () => {
    try {
      const response = await api.get('/email-templates/categories');
      const categories = response.data.data;
      setEmailCategories(Array.isArray(categories) ? categories : []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setEmailCategories([]);
    }
  };

  const loadEmailCategoryDetails = async () => {
    try {
      const response = await api.get('/email-templates/categories/details');
      const payload = response.data?.data;
      const details = Array.isArray(payload) ? payload : (payload?.categories ?? []);
      setEmailCategoryDetails(Array.isArray(details) ? details : []);
    } catch (error) {
      console.error('Erro ao carregar detalhes das categorias:', error);
      setEmailCategoryDetails([]);
    }
  };

  const loadEmailDefaultVariables = async () => {
    try {
      const response = await api.get('/email-templates/variables');
      const variables = response.data.data;
      // Converter objeto para array de strings
      const variablesArray = typeof variables === 'object' && !Array.isArray(variables) 
        ? Object.values(variables).flat() 
        : Array.isArray(variables) ? variables : [];
      setEmailDefaultVariables(variablesArray);
    } catch (error) {
      console.error('Erro ao carregar variáveis padrão:', error);
      setEmailDefaultVariables([]);
    }
  };

  const saveEmailTemplate = async () => {
    try {
      setSavingEmailTemplate(true);
      
      if (selectedEmailTemplate) {
        await api.put(`/email-templates/${selectedEmailTemplate.id}`, emailForm);
        toast.success('Template atualizado com sucesso!');
      } else {
        await api.post('/email-templates', emailForm);
        toast.success('Template criado com sucesso!');
      }
      
      await loadEmailTemplates();
      setShowEmailModal(false);
      setSelectedEmailTemplate(null);
      setEmailForm({
        name: '',
        subject: '',
        content: '',
        category: '',
        variables: []
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar template');
    } finally {
      setSavingEmailTemplate(false);
    }
  };

  const testEmailTemplate = async () => {
    if (!testEmailAddress) {
      toast.error('Digite um email para teste');
      return;
    }

    try {
      setTestingEmailTemplate(true);
      
      // Se estamos editando um template existente, usar o ID dele
      // Se estamos criando um novo, primeiro salvar e depois testar
      let templateId = selectedEmailTemplate?.id;
      
      if (!templateId) {
        // Salvar o template primeiro
        const response = await api.post('/email-templates', emailForm);
        templateId = response.data.data.id;
        toast.success('Template criado com sucesso!');
      }
      
      // Criar variáveis de teste baseadas nas variáveis do template
      const testVariables: Record<string, string> = {};
      emailForm.variables.forEach(variable => {
        switch (variable) {
          case 'nome':
            testVariables[variable] = 'João Silva';
            break;
          case 'numeroTitulo':
            testVariables[variable] = 'TIT-001';
            break;
          case 'valor':
            testVariables[variable] = 'R$ 1.500,00';
            break;
          case 'vencimento':
            testVariables[variable] = '31/12/2024';
            break;
          case 'urlBoleto':
            testVariables[variable] = 'https://boleto.exemplo.com/tit001';
            break;
          case 'empresa':
            testVariables[variable] = 'Empresa Teste Ltda';
            break;
          case 'telefone':
            testVariables[variable] = '(11) 99999-9999';
            break;
          case 'valorAtualizado':
            testVariables[variable] = 'R$ 1.650,00';
            break;
          case 'valorNegociado':
            testVariables[variable] = 'R$ 1.200,00';
            break;
          case 'parcelas':
            testVariables[variable] = '3';
            break;
          case 'valorParcela':
            testVariables[variable] = 'R$ 400,00';
            break;
          default:
            testVariables[variable] = `[${variable}]`;
        }
      });
      
      // Enviar email de teste
      await api.post('/email/test-template', {
        templateId,
        testEmail: testEmailAddress,
        variables: testVariables
      });
      
      toast.success('Email de teste enviado com sucesso!');
      setTestEmailAddress('');
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao enviar email de teste');
    } finally {
      setTestingEmailTemplate(false);
    }
  };

  // Funções para Personalização
  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error('O arquivo deve ter menos de 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };



  const handleSaveCustomization = async () => {
    setSavingCustomization(true);
    try {
      const formData = new FormData();
      formData.append('systemName', customization.systemName);
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      await api.post('/config/customization', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Personalização salva com sucesso!');
      
      // Aplicar a personalização em tempo real usando o contexto
      applyCustomization(customization);
      
      // Salvar no localStorage para persistir
      localStorage.setItem('systemCustomization', JSON.stringify(customization));
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar personalização');
    } finally {
      setSavingCustomization(false);
    }
  };

  const handleResetCustomization = () => {
    setCustomization({
      systemName: 'Cobrança'
    });
    setLogoFile(null);
    setLogoPreview('');
  };

  const deleteEmailTemplate = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      await api.delete(`/email-templates/${id}`);
      toast.success('Template excluído com sucesso!');
      await loadEmailTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir template');
    }
  };

  // Funções para gerenciar categorias
  const saveCategory = async () => {
    try {
      setSavingCategory(true);
      
      if (selectedCategory) {
        await api.put(`/email-templates/categories/${selectedCategory.id}`, categoryForm);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        await api.post('/email-templates/categories', categoryForm);
        toast.success('Categoria criada com sucesso!');
      }
      
      await loadEmailCategoryDetails();
      await loadEmailCategories();
      setShowCategoryModal(false);
      setSelectedCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        color: '#3b82f6'
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar categoria');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta categoria?')) return;

    try {
      await api.delete(`/email-templates/categories/${id}`);
      toast.success('Categoria excluída com sucesso!');
      await loadEmailCategoryDetails();
      await loadEmailCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir categoria');
    }
  };

  // Funções para Integração WhatsApp
  const loadIntegrationConfig = async () => {
    try {
      setIntegrationLoading(true);
      const response = await api.get('/whatsapp-integration/config');
      if (response.data.success && response.data.data) {
        setIntegrationConfig(response.data.data);
      }
    } catch (error) {
      console.log('Configuração de integração WhatsApp não encontrada');
    } finally {
      setIntegrationLoading(false);
    }
  };

  // Funções WhatsApp Automação
  const loadWaCompanies = async () => {
    try {
      const res = await api.get('/companies?isActive=true');
      const data = res.data?.data;
      const companiesList = Array.isArray(data) ? data : (data?.companies ?? []);
      setWaCompanies(companiesList);
    } catch (e) {
      console.warn('Não foi possível carregar empresas');
    }
  };

  const loadWaTemplates = async () => {
    try {
      const res = await api.get('/whatsapp-integration/templates');
      const templates = res.data?.data?.templates;
      setWaTemplates(Array.isArray(templates) ? templates : []);
    } catch (e) {
      console.warn('Não foi possível carregar templates de WhatsApp');
      setWaTemplates([]);
    }
  };

  const loadWaAutomations = async () => {
    try {
      const q = waSelectedCompany ? `?companyId=${waSelectedCompany}` : '';
      const res = await api.get(`/whatsapp-automation${q}`);
      setWaAutomations(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error('Erro ao carregar automações de WhatsApp');
      setWaAutomations([]);
    }
  };

  const saveWaAutomation = async () => {
    try {
      const payload = { ...waForm, companyId: waForm.companyId || (waSelectedCompany || undefined), templateId: waForm.templateId || undefined };
      if (waSelected) {
        await api.put(`/whatsapp-automation/${waSelected.id}`, payload);
      } else {
        await api.post('/whatsapp-automation', payload);
      }
      setWaShowModal(false);
      setWaSelected(null);
      setWaForm({
        name: '', description: '', triggerType: '', triggerValue: 0, templateId: '', isActive: true,
        conditions: [], segments: [], schedule: { type: 'immediate' }, retryConfig: { maxRetries: 3, retryDelay: 5 },
        throttling: { maxMessagesPerHour: 1000, maxMessagesPerDay: 10000 }, companyId: ''
      });
      await loadWaAutomations();
      toast.success('Automação salva com sucesso');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar automação');
    }
  };

  const toggleWaAutomation = async (id: string) => {
    try {
      await api.patch(`/whatsapp-automation/${id}/toggle`);
      await loadWaAutomations();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const executeWaAutomation = async (id: string) => {
    try {
      const res = await api.post(`/whatsapp-automation/${id}/execute`);
      const r = res.data?.data;
      toast.success(`Execução iniciada. Enviados: ${r?.messagesSent || 0}`);
      await loadWaAutomations();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao executar automação');
    }
  };

  const deleteWaAutomation = async (id: string) => {
    if (!confirm('Deseja excluir esta automação?')) return;
    try {
      await api.delete(`/whatsapp-automation/${id}`);
      await loadWaAutomations();
      toast.success('Automação excluída');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao excluir automação');
    }
  };

  const openWaEdit = (a: WhatsAppAutomation) => {
    setWaSelected(a);
    setWaForm({
      name: a.name,
      description: a.description || '',
      triggerType: a.triggerType,
      triggerValue: a.triggerValue,
      templateId: a.templateId || '',
      isActive: a.isActive,
      conditions: a.conditions || [],
      segments: a.segments || [],
      schedule: a.schedule || { type: 'immediate' },
      retryConfig: a.retryConfig || { maxRetries: 3, retryDelay: 5 },
      throttling: a.throttling || { maxMessagesPerHour: 1000, maxMessagesPerDay: 10000 },
      companyId: a.companyId || ''
    });
    setWaShowModal(true);
  };

  const viewWaExecutions = async (a: WhatsAppAutomation) => {
    try {
      const res = await api.get(`/whatsapp-automation/${a.id}/executions`);
      setWaExecutions(Array.isArray(res.data?.data) ? res.data.data : []);
      setWaExecModal(true);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao carregar execuções');
    }
  };

  const viewWaStats = async (a: WhatsAppAutomation) => {
    try {
      const res = await api.get(`/whatsapp-automation/${a.id}/stats`);
      setWaStats(res.data?.data || null);
      setWaStatsModal(true);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao carregar estatísticas');
    }
  };

  const refreshWaPreviewVarsFromTemplate = (templateId?: string | null) => {
    if (!templateId) { setWaPreviewVars({}); return; }
    const t = waTemplates.find((x: any) => x.id === templateId);
    const vars = Array.isArray(t?.variables) ? (t.variables as string[]) : [];
    const next: Record<string, string> = {};
    vars.forEach(k => { next[k] = waPreviewVars[k] || ''; });
    setWaPreviewVars(next);
  };



  const parseWaVariablesFromContent = (content: string): string[] => {
    const vars = new Set<string>();
    const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(content)) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  };

  const openWaTemplateModal = (template?: any) => {
    if (template) {
      setWaTemplateSelected(template);
      const variables = Array.isArray(template.variables) ? template.variables : [];
      setWaTemplateForm({
        name: template.name || '',
        category: template.category || '',
        content: template.content || '',
        variables,
        isActive: !!template.isActive,
      });
      const vmap: Record<string, string> = {};
      variables.forEach((k: string) => { vmap[k] = waPreviewVars[k] || ''; });
      setWaPreviewVars(vmap);
    } else {
      setWaTemplateSelected(null);
      setWaTemplateForm({ name: '', category: '', content: '', variables: [], isActive: true });
      setWaPreviewVars({});
    }
    setWaTemplateTestPhone('');
    setWaTemplateModal(true);
  };

  const detectWaTemplateVariables = () => {
    const vars = parseWaVariablesFromContent(waTemplateForm.content);
    setWaTemplateForm(prev => ({ ...prev, variables: vars }));
    const vmap: Record<string, string> = {};
    vars.forEach((k: string) => { vmap[k] = waPreviewVars[k] || ''; });
    setWaPreviewVars(vmap);
  };



  const saveWaTemplate = async () => {
    try {
      setWaTemplateSaving(true);
      if (waTemplateSelected?.id) {
        await api.put(`/whatsapp-integration/templates/${waTemplateSelected.id}`, waTemplateForm);
      } else {
        await api.post('/whatsapp-integration/templates', waTemplateForm);
      }
      await loadWaTemplates();
      setWaTemplateModal(false);
      toast.success('Template salvo com sucesso');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao salvar template');
    } finally {
      setWaTemplateSaving(false);
    }
  };

  const deleteWaTemplate = async (id: string) => {
    if (!confirm('Deseja excluir este template?')) return;
    try {
      await api.delete(`/whatsapp-integration/templates/${id}`);
      await loadWaTemplates();
      toast.success('Template excluído');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao excluir template');
    }
  };

  const testSendWaTemplate = async () => {
    if (!waTemplateSelected?.id) {
      toast.error('Salve o template antes de enviar o teste');
      return;
    }
    if (!waTemplateTestPhone.trim()) {
      toast.error('Informe um telefone para teste');
      return;
    }
    try {
      await api.post('/whatsapp-integration/messages/template', {
        to: waTemplateTestPhone.trim(),
        templateId: waTemplateSelected.id,
        variables: waPreviewVars,
      });
      toast.success('Mensagem de teste enviada');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao enviar teste');
    }
  };

  const handleIntegrationSave = async () => {
    try {
      setIntegrationLoading(true);
      await api.put('/whatsapp-integration/config', integrationConfig);
      toast.success('Configuração de integração WhatsApp salva com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setIntegrationLoading(false);
    }
  };

  const testIntegrationConnection = async () => {
    try {
      setIntegrationLoading(true);
      await api.post('/whatsapp-integration/test-connection');
      toast.success('Conexão testada com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao testar conexão');
    } finally {
      setIntegrationLoading(false);
    }
  };

  // Filtros de email templates
  const filteredEmailTemplates = emailTemplates.filter(template => {
    const matchesSearch = !emailFilters.search || 
      template.name.toLowerCase().includes(emailFilters.search.toLowerCase()) ||
      template.subject.toLowerCase().includes(emailFilters.search.toLowerCase()) ||
      template.content.toLowerCase().includes(emailFilters.search.toLowerCase());
    
    const matchesCategory = !emailFilters.category || template.category === emailFilters.category;
    
    return matchesSearch && matchesCategory;
  });

  // Componente da aba WhatsApp
  const WhatsAppConfigTab: React.FC = () => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
          <div className="text-lg">Carregando configuração WhatsApp...</div>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Configuração WhatsApp Multi-Provedor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Provedor
                </label>
                <Select
                  value={config.provider}
                  onValueChange={(value) => setConfig(prev => ({ ...prev, provider: value }))}
                >
                  <option value="twilio">Twilio</option>
                  <option value="messagebird">MessageBird</option>
                  <option value="360dialog">360dialog</option>
                  <option value="meta">Meta</option>
                </Select>
        </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Número de Telefone
                </label>
                <Input
                  value={config.phoneNumber}
                  onChange={(e) => setConfig(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder="+5511999999999"
                  className={errors.phoneNumber ? 'border-red-500' : ''}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Account SID
                </label>
                <Input
                  value={config.accountSid || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, accountSid: e.target.value }))}
                  placeholder="Account SID"
                  className={errors.accountSid ? 'border-red-500' : ''}
                />
                {errors.accountSid && (
                  <p className="text-red-500 text-xs mt-1">{errors.accountSid}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Auth Token
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={config.authToken || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, authToken: e.target.value }))}
                    placeholder="Auth Token"
                    className={errors.authToken ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.authToken && (
                  <p className="text-red-500 text-xs mt-1">{errors.authToken}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Webhook URL
                </label>
                <Input
                  value={config.webhookUrl || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://seu-dominio.com/webhook"
                  className={errors.webhookUrl ? 'border-red-500' : ''}
                />
                {errors.webhookUrl && (
                  <p className="text-red-500 text-xs mt-1">{errors.webhookUrl}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={config.isActive}
                onChange={(e) => setConfig(prev => ({ ...prev, isActive: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="isActive" className="text-sm text-foreground">
                Ativar WhatsApp
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Configuração'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleTestConnection} 
                disabled={loading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Testar Conexão
        </Button>
      </div>
          </CardContent>
        </Card>

        {/* Status da conexão */}
        <Card>
          <CardHeader>
            <CardTitle>Status da Conexão</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              {config.isActive ? (
                <>
                  <Badge className="bg-green-100 text-green-800">
                    <MessageCircle className="w-3 h-3 mr-1" />
                    Ativo
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    WhatsApp está ativo e pronto para uso
                  </span>
                </>
              ) : (
                <>
                  <Badge className="bg-muted text-gray-800 dark:text-slate-200">
                    <MessageCircle className="w-3 h-3 mr-1" />
                    Inativo
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Configure e ative o WhatsApp para começar a usar
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Usuários autorizados */}
        <Card>
          <CardHeader>
            <CardTitle>Usuários Autorizados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {users.map(user => (
                <div key={user.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`user-${user.id}`}
                    checked={config.allowedUsers.includes(user.id)}
                    onChange={() => toggleUser(user.id)}
                    className="rounded"
                  />
                  <label htmlFor={`user-${user.id}`} className="text-sm">
                    {user.name} ({user.email})
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle>Estatísticas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.totalMessages}</div>
                  <div className="text-sm text-muted-foreground">Total de Mensagens</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.sentToday}</div>
                  <div className="text-sm text-muted-foreground">Enviadas Hoje</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.successRate}%</div>
                  <div className="text-sm text-muted-foreground">Taxa de Sucesso</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">Última Mensagem</div>
                  <div className="text-xs text-muted-foreground">{stats.lastMessage}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Componente da aba Integração WhatsApp
  const WhatsAppIntegrationTab: React.FC = () => {
    if (integrationLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Carregando configuração...</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Configuração da Integração WhatsApp
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  URL da API
                </label>
                <Input
                  type="url"
                  value={integrationConfig.baseUrl}
                  onChange={(e) => setIntegrationConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="https://enterprise-207api.gestorleads.com.br"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  URL base da API do GestorLeads
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  API ID
                </label>
                <Input
                  value={integrationConfig.apiId}
                  onChange={(e) => setIntegrationConfig(prev => ({ ...prev, apiId: e.target.value }))}
                  placeholder="ID da API GestorLeads"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  ID da API usado nos endpoints do GestorLeads
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  API Token
                </label>
                <Input
                  type="password"
                  value={integrationConfig.apiToken}
                  onChange={(e) => setIntegrationConfig(prev => ({ ...prev, apiToken: e.target.value }))}
                  placeholder="Bearer Token de autenticação"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Token de autenticação (Bearer Token) fornecido pelo GestorLeads
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Webhook URL <span className="text-gray-400">(Opcional)</span>
                </label>
                <Input
                  type="url"
                  value={integrationConfig.webhookUrl}
                  onChange={(e) => setIntegrationConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://seu-dominio.com/webhook"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  URL pública para receber notificações do GestorLeads
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="integrationIsActive"
                checked={integrationConfig.isActive}
                onChange={(e) => setIntegrationConfig(prev => ({ ...prev, isActive: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="integrationIsActive" className="text-sm text-foreground">
                Ativar integração
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button onClick={handleIntegrationSave} disabled={integrationLoading}>
                <Save className="w-4 h-4 mr-2" />
                {integrationLoading ? 'Salvando...' : 'Salvar Configuração'}
              </Button>
              <Button 
                variant="outline" 
                onClick={testIntegrationConnection} 
                disabled={integrationLoading || !integrationConfig.baseUrl || !integrationConfig.apiId || !integrationConfig.apiToken}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Testar Conexão
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status da Integração</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              {integrationConfig.isActive ? (
                <>
                  <Badge className="bg-green-100 text-green-800">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Ativo
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Integração WhatsApp está ativa e pronta para uso
                  </span>
                </>
              ) : (
                <>
                  <Badge className="bg-muted text-gray-800">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Inativo
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Configure e ative a integração para começar a usar
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recursos Disponíveis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Mail className="w-8 h-8 text-blue-600" />
                <div>
                  <h4 className="font-medium">Templates</h4>
                  <p className="text-sm text-muted-foreground">Mensagens pré-definidas</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <Users className="w-8 h-8 text-green-600" />
                <div>
                  <h4 className="font-medium">Contatos</h4>
                  <p className="text-sm text-muted-foreground">Gerenciar contatos</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <BarChart3 className="w-8 h-8 text-purple-600" />
                <div>
                  <h4 className="font-medium">Estatísticas</h4>
                  <p className="text-sm text-muted-foreground">Relatórios de uso</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Aba: WhatsApp Automação
  const WhatsAppAutomationTab: React.FC = () => {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Automação de WhatsApp (Multiempresa)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-foreground text-sm">
              Configure o envio automático de mensagens de WhatsApp por empresa. Você pode integrar diferentes provedores
              e definir gatilhos, condições e agendamentos semelhantes aos de e-mail.
            </p>
            <div className="grid md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Empresa</label>
                <Select value={waSelectedCompany} onValueChange={(v) => { setWaSelectedCompany(v); }}>
                  <option value="">Todas</option>
                  {waCompanies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </div>
              <Button variant="outline" onClick={loadWaAutomations}>Atualizar</Button>
              <Button variant="outline" onClick={() => navigate('/whatsapp-integration')}>Integração WhatsApp</Button>
              <Button onClick={() => { setWaSelected(null); setWaShowModal(true); }}>Nova Automação</Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Como funciona</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc ml-5 text-sm space-y-1">
              <li>Canal por empresa: cada empresa pode ter sua própria API/instância de WhatsApp configurada.</li>
              <li>Templates de WhatsApp (aprovados pelo provedor) com variáveis e mídia opcional.</li>
              <li>Gatilhos, condições, segmentos, agendamento, retry e throttling similares ao e-mail.</li>
              <li>Rastreamento via webhooks (entregue, lido) e links rastreados para cliques.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Automações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">Gerencie templates de WhatsApp usados nas automações.</div>
                <Button variant="outline" onClick={() => openWaTemplateModal()}>Novo Template</Button>
              </div>
              <div className="border rounded p-3">
                {waTemplates.length === 0 ? (
                  <div className="text-xs text-muted-foreground">Nenhum template cadastrado.</div>
                ) : (
                  <div className="space-y-2">
                    {waTemplates.map((t: any) => (
                      <div key={t.id} className="flex items-center justify-between border border-border rounded p-2 bg-card">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{t.name} {t.isActive ? <Badge className="ml-2">Ativo</Badge> : <Badge variant="secondary" className="ml-2">Inativo</Badge>}</div>
                          <div className="text-xs text-muted-foreground truncate">{t.content}</div>
                          {Array.isArray(t.variables) && t.variables.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {t.variables.map((v: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-[10px]">{v}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-2 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => openWaTemplateModal(t)}>Editar</Button>
                          <Button variant="outline" size="sm" onClick={() => { setWaTemplateSelected(t); setWaTemplateModal(true); }}>Preview</Button>
                          <Button variant="destructive" size="sm" onClick={() => deleteWaTemplate(t.id)}>Excluir</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {waAutomations.map(a => (
                <div key={a.id} className="p-3 border rounded flex items-center justify-between">
                  <div>
                    <div className="font-medium">{a.name} {a.isActive ? <Badge className="ml-2">Ativa</Badge> : <Badge variant="secondary" className="ml-2">Inativa</Badge>}</div>
                    <div className="text-xs text-muted-foreground">{a.description}</div>
                    <div className="text-xs text-muted-foreground mt-1">Trigger: {a.triggerType} • Execuções: {a._count?.executions || 0}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openWaEdit(a)}>Editar</Button>
                    <Button variant="outline" size="sm" onClick={() => toggleWaAutomation(a.id)}>{a.isActive ? 'Desativar' : 'Ativar'}</Button>
                    <Button variant="outline" size="sm" onClick={() => executeWaAutomation(a.id)}>Executar</Button>
                    <Button variant="outline" size="sm" onClick={() => viewWaExecutions(a)}>Execuções</Button>
                    <Button variant="outline" size="sm" onClick={() => viewWaStats(a)}>Estatísticas</Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteWaAutomation(a.id)}>Excluir</Button>
                  </div>
                </div>
              ))}
              {waAutomations.length === 0 && (
                <div className="text-sm text-muted-foreground">Nenhuma automação encontrada.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Funções para Limpeza de Dados
  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data.data.companies || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    }
  };

  const handleCleanupData = async () => {
    if (!selectedCompany) {
      toast.error('Selecione uma empresa');
      return;
    }

    if (cleanupConfirmation !== 'LIMPAR') {
      toast.error('Digite "LIMPAR" para confirmar a ação');
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await api.delete(`/companies/${selectedCompany}/clear-data`);
      
      toast.success(response.data.message);
      setShowCleanupModal(false);
      setSelectedCompany('');
      setCleanupConfirmation('');
      
      // Recarregar empresas
      await loadCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao limpar dados da empresa');
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Navegação das Abas */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('geral')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'geral'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Geral
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'whatsapp'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            WhatsApp
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'emails'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            E-mails
          </button>
          <button
            onClick={() => setActiveTab('whatsapp-automation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'whatsapp-automation'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            WhatsApp Automação
          </button>
          <button
            onClick={() => setActiveTab('whatsapp-integration')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'whatsapp-integration'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Integração WhatsApp
          </button>
          <button
            onClick={() => setActiveTab('data-cleanup')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'data-cleanup'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Limpeza de Dados
          </button>
          <button
            onClick={() => setActiveTab('operador-metas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'operador-metas'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Metas dos Operadores
          </button>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('notifications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notifications'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Bell className="w-4 h-4 inline mr-2" />
              Notificações
            </button>
          )}
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('external-api')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'external-api'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              API Externa
            </button>
          )}
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('personalizacao')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'personalizacao'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Image className="w-4 h-4 inline mr-2" />
              Personalização
            </button>
          )}
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('rique-atendimento')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rique-atendimento'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Bot className="w-4 h-4 inline mr-2" />
              Rique Atendimento
            </button>
          )}
        </nav>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === 'geral' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
                <Server className="w-5 h-5 mr-2" />
              Informações do Sistema
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
              {/* Informações do Usuário */}
              <div className="space-y-3 border-b pb-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Usuário:</span>
                  <span className="text-sm font-medium">{user?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <span className="text-sm font-medium">{user?.email || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Função:</span>
                  <span className="text-sm font-medium">{user?.role || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <span className="text-sm font-medium text-green-600">Sistema funcionando normalmente</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Última atualização:</span>
                  <span className="text-sm font-medium">{new Date().toLocaleString('pt-BR')}</span>
                </div>
              </div>

              {/* Informações Técnicas */}
              {systemInfo ? (
                <div className="space-y-3">
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Uptime:</span>
                  <span className="text-sm font-medium">{systemInfo.uptime}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Versão:</span>
                    <span className="text-sm font-medium">{systemInfo.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Ambiente:</span>
                    <span className="text-sm font-medium">{systemInfo.environment}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Memória:</span>
                      <span className="text-sm font-medium">{systemInfo.memory.used} / {systemInfo.memory.total}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-2">
                  <div 
                        className="bg-primary h-2 rounded-full" 
                    style={{ width: `${systemInfo.memory.percentage}%` }}
                  ></div>
                </div>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Banco de Dados:</span>
                    <span className={`text-sm font-medium ${systemInfo.database.status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                      {systemInfo.database.status === 'connected' ? 'Conectado' : 'Desconectado'}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Conexões DB:</span>
                    <span className="text-sm font-medium">{systemInfo.database.connections}</span>
                </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-muted-foreground mt-2">Carregando...</p>
                </div>
            )}
          </CardContent>
        </Card>

          {/* Configurações Gerais */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Configurações do Sistema
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {configs.length > 0 ? (
                <div className="space-y-3">
                  {configs.map((config) => (
                    <div key={config.key} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium text-sm">{config.key}</div>
                        <div className="text-xs text-muted-foreground">{config.description || 'Sem descrição'}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">{config.value}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedConfig(config);
                            setConfigValue(config.value);
                            setShowConfigModal(true);
                          }}
                        >
                          Editar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma configuração encontrada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Aba WhatsApp */}
      {activeTab === 'whatsapp' && (
        <WhatsAppConfigTab />
      )}

      {/* Aba Integração WhatsApp */}
      {activeTab === 'whatsapp-integration' && (
        <WhatsAppIntegrationTab />
      )}

      {/* Aba Limpeza de Dados */}
      {activeTab === 'data-cleanup' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <RefreshCw className="w-5 h-5 mr-2" />
                Limpeza de Dados de Empresa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        ⚠️ ATENÇÃO: Esta ação é irreversível!
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>Esta funcionalidade irá remover permanentemente todos os dados da empresa selecionada, incluindo:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Títulos e histórico de pagamentos</li>
                          <li>Clientes e fornecedores</li>
                          <li>Feedbacks e contatos</li>
                          <li>Comprovantes e arquivos de áudio</li>
                          <li>Templates de email e WhatsApp</li>
                          <li>Automações e configurações</li>
                          <li>Webhooks e integrações</li>
                        </ul>
                        <p className="mt-2 font-medium">A empresa deve estar inativa para que esta operação seja permitida.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Selecione a Empresa
                    </label>
                    <Select
                      value={selectedCompany}
                      onValueChange={(value) => setSelectedCompany(value)}
                    >
                      <option value="">Selecione uma empresa inativa</option>
                      {companies
                        .filter(company => !company.isActive)
                        .map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name} (Inativa)
                          </option>
                        ))}
                    </Select>
                    {companies.filter(company => !company.isActive).length === 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Nenhuma empresa inativa encontrada. Inative uma empresa primeiro para poder limpar seus dados.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => setShowCleanupModal(true)}
                      disabled={!selectedCompany}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Limpar Dados da Empresa
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'whatsapp-automation' && (
        <WhatsAppAutomationTab />
      )}

      {/* Conteúdo da aba E-mails */}
      {activeTab === 'emails' && (
        <div className="space-y-6">
          {/* Acesso rápido aos módulos de E-mail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Módulos de E-mail
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => navigate('/email-automation')}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-blue-600" />
                    <div>
                      <div className="font-medium">Automação de Emails</div>
                      <div className="text-xs text-muted-foreground">Configurar e executar automações</div>
                    </div>
                  </div>
                  <span className="text-gray-400">›</span>
                </button>

                <button
                  onClick={() => navigate('/email-reports')}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                    <div>
                      <div className="font-medium">Relatórios de Email</div>
                      <div className="text-xs text-muted-foreground">Métricas e exportações</div>
                    </div>
                  </div>
                  <span className="text-gray-400">›</span>
                </button>

                <button
                  onClick={() => navigate('/email-ab-testing')}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition"
                >
                  <div className="flex items-center gap-3">
                    <Target className="w-6 h-6 text-purple-600" />
                    <div>
                      <div className="font-medium">A/B Testing</div>
                      <div className="text-xs text-muted-foreground">Experimentos com variantes</div>
                    </div>
                  </div>
                  <span className="text-gray-400">›</span>
                </button>

                <button
                  onClick={() => navigate('/email-personalization')}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition"
                >
                  <div className="flex items-center gap-3">
                    <Code className="w-6 h-6 text-indigo-600" />
                    <div>
                      <div className="font-medium">Personalização</div>
                      <div className="text-xs text-muted-foreground">Variáveis e templates</div>
                    </div>
                  </div>
                  <span className="text-gray-400">›</span>
                </button>

                <button
                  onClick={() => navigate('/email-analytics')}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition"
                >
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-teal-600" />
                    <div>
                      <div className="font-medium">Analytics de Email</div>
                      <div className="text-xs text-muted-foreground">Painel de performance</div>
                    </div>
                  </div>
                  <span className="text-gray-400">›</span>
                </button>
              </div>
            </CardContent>
          </Card>
          {/* Cabeçalho com Filtros */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Templates de E-mail
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  placeholder="Buscar templates..."
                  value={emailFilters.search}
                  onChange={(e) => setEmailFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="flex-1"
                />
                <Select
                  value={emailFilters.category}
                  onValueChange={(value) => setEmailFilters(prev => ({ ...prev, category: value }))}
                  className="w-full sm:w-48"
                >
                  <option value="">Todas as categorias</option>
                  {Array.isArray(emailCategories) && emailCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Select>
                <Button onClick={() => setShowEmailModal(true)}>
                  Novo Template
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Gerenciamento de Categorias */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Categorias
                </CardTitle>
                <Button onClick={() => setShowCategoryModal(true)}>
                  Nova Categoria
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {emailCategoryDetails.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <div>
                        <div className="font-medium text-sm">{category.name}</div>
                        {category.description && (
                          <div className="text-xs text-muted-foreground">{category.description}</div>
                        )}
                      </div>
                    </div>
                        <div className="flex items-center space-x-2">
                      {category.isDefault && (
                        <Badge variant="secondary" className="text-xs">
                          Padrão
                          </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedCategory(category);
                          setCategoryForm({
                            name: category.name,
                            description: category.description || '',
                            color: category.color
                          });
                          setShowCategoryModal(true);
                        }}
                      >
                        Editar
                      </Button>
                      {!category.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteCategory(category.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Excluir
                        </Button>
                      )}
                        </div>
                  </div>
                ))}
              </div>
              {emailCategoryDetails.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma categoria encontrada</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lista de Templates */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredEmailTemplates.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <Badge variant="outline">
                      {template.category}
                    </Badge>
                      </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Assunto:</p>
                    <p className="text-sm text-muted-foreground">{template.subject}</p>
                        </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Conteúdo:</p>
                    <p className="text-sm text-muted-foreground line-clamp-3">{template.content}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Variáveis:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {template.variables.map((variable, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {variable}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2 pt-3">
                        <Button
                          variant="outline"
                          size="sm"
                      onClick={() => {
                        setSelectedEmailTemplate(template);
                        setEmailForm({
                          name: template.name,
                          subject: template.subject,
                          content: template.content,
                          category: template.category,
                          variables: template.variables
                        });
                        setShowEmailModal(true);
                      }}
                      className="flex-1"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEmailTemplate(template.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Excluir
                        </Button>
                      </div>
                </CardContent>
              </Card>
            ))}
                    </div>

          {filteredEmailTemplates.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Mail className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-foreground mb-2">Nenhum template encontrado</h3>
                <p className="text-muted-foreground mb-4">
                  {emailTemplates.length === 0 
                    ? "Crie seu primeiro template de e-mail para começar."
                    : "Tente ajustar os filtros de busca."
                  }
                </p>
                <Button onClick={() => setShowEmailModal(true)}>
                  Criar Primeiro Template
                </Button>
            </CardContent>
          </Card>
          )}
        </div>
      )}

      {/* Modal de Configuração */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Editar Configuração"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {selectedConfig?.key}
            </label>
            <Input
              value={configValue}
              onChange={(e) => setConfigValue(e.target.value)}
              placeholder="Valor da configuração"
            />
            {selectedConfig?.description && (
              <p className="text-xs text-muted-foreground mt-1">{selectedConfig.description}</p>
            )}
      </div>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={updateConfig} disabled={savingConfig}>
              {savingConfig ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Template de Email */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setSelectedEmailTemplate(null);
          setEmailForm({
            name: '',
            subject: '',
            content: '',
            category: '',
            variables: []
          });
        }}
        title={selectedEmailTemplate ? 'Editar Template' : 'Novo Template'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Nome do Template
              </label>
            <Input
                value={emailForm.name}
                onChange={(e) => setEmailForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome do template"
            />
          </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Categoria
              </label>
              <Select
                value={emailForm.category}
                onValueChange={(value) => setEmailForm(prev => ({ ...prev, category: value }))}
              >
                <option value="">Selecione uma categoria</option>
                {Array.isArray(emailCategories) && emailCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Assunto
            </label>
            <Input
              value={emailForm.subject}
              onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Assunto do e-mail"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Conteúdo
            </label>
            <textarea
              value={emailForm.content}
              onChange={(e) => setEmailForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Conteúdo do e-mail..."
              className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Variáveis Disponíveis
            </label>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(emailDefaultVariables) && emailDefaultVariables.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  onClick={() => {
                    if (!emailForm.variables.includes(variable)) {
                      setEmailForm(prev => ({
                        ...prev,
                        variables: [...prev.variables, variable]
                      }));
                    }
                  }}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                >
                  {variable}
                </button>
              ))}
            </div>
            {emailForm.variables.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-muted-foreground mb-1">Variáveis selecionadas:</p>
                <div className="flex flex-wrap gap-1">
                  {emailForm.variables.map((variable, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {variable}
                      <button
                        type="button"
                        onClick={() => {
                          setEmailForm(prev => ({
                            ...prev,
                            variables: prev.variables.filter((_, i) => i !== index)
                          }));
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
            </div>
          )}
          </div>
          
          {/* Seção de Teste do Template */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Testar Template</h4>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Email para Teste
                </label>
                <Input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="seu-email@exemplo.com"
                  className="text-sm"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={testEmailTemplate}
                disabled={testingEmailTemplate || !testEmailAddress || !emailForm.name || !emailForm.subject || !emailForm.content}
                className="text-sm"
              >
                {testingEmailTemplate ? 'Enviando...' : 'Enviar Teste'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Envie um email de teste para validar o template antes de salvar
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEmailModal(false);
                setSelectedEmailTemplate(null);
                setEmailForm({
                  name: '',
                  subject: '',
                  content: '',
                  category: '',
                  variables: []
                });
                setTestEmailAddress('');
              }}
            >
              Cancelar
            </Button>
            <Button onClick={saveEmailTemplate} disabled={savingEmailTemplate}>
              {savingEmailTemplate ? 'Salvando...' : 'Salvar Template'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Categoria */}
      <Modal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setSelectedCategory(null);
          setCategoryForm({
            name: '',
            description: '',
            color: '#3b82f6'
          });
        }}
        title={selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Nome da Categoria
            </label>
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nome da categoria"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Descrição (Opcional)
            </label>
            <Input
              value={categoryForm.description}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrição da categoria"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Cor da Categoria
            </label>
            <Input
              type="color"
              value={categoryForm.color}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
              className="w-full"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Cancelar
            </Button>
            <Button onClick={saveCategory} disabled={savingCategory}>
              {savingCategory ? 'Salvando...' : 'Salvar Categoria'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal - Criar/Editar Automação WhatsApp */}
      <Modal
        isOpen={waShowModal}
        onClose={() => { setWaShowModal(false); setWaSelected(null); }}
        title={waSelected ? 'Editar Automação WhatsApp' : 'Nova Automação WhatsApp'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
              <Input value={waForm.name} onChange={(e) => setWaForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Empresa</label>
              <Select value={waForm.companyId || ''} onValueChange={(v) => setWaForm(prev => ({ ...prev, companyId: v || undefined }))}>
                <option value="">(Opcional)</option>
                {waCompanies.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input value={waForm.description} onChange={(e) => setWaForm(prev => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tipo de Trigger</label>
              <Select value={waForm.triggerType} onValueChange={(v) => setWaForm(prev => ({ ...prev, triggerType: v }))}>
                <option value="">Selecione</option>
                <option value="days_before_due">Dias antes do vencimento</option>
                <option value="days_after_due">Dias após o vencimento</option>
                <option value="status_change">Mudança de status</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Valor do Trigger</label>
              <Input type="number" value={waForm.triggerValue} onChange={(e) => setWaForm(prev => ({ ...prev, triggerValue: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Template WhatsApp</label>
              <Select value={waForm.templateId || ''} onValueChange={(v) => { setWaForm(prev => ({ ...prev, templateId: v })); refreshWaPreviewVarsFromTemplate(v); }}>
                <option value="">(Opcional)</option>
                {waTemplates.map((t: any) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setWaShowModal(false); setWaSelected(null); }}>Cancelar</Button>
            <Button onClick={saveWaAutomation}>Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Modal - Execuções */}
      <Modal
        isOpen={waExecModal}
        onClose={() => setWaExecModal(false)}
        title="Execuções da Automação"
      >
        <div className="max-h-[60vh] overflow-y-auto text-sm">
          {waExecutions.map((e, i) => (
            <div key={i} className="flex justify-between py-2 border-b">
              <div>
                <div className="font-medium">{new Date(e.createdAt).toLocaleString('pt-BR')}</div>
                <div className="text-xs text-muted-foreground">Status: {e.status}</div>
              </div>
              <div className="text-right">
                <div>Enviadas: {e.messagesSent}</div>
                <div>Falhas: {e.messagesFailed}</div>
                <div>Tempo: {e.executionTime}ms</div>
              </div>
            </div>
          ))}
          {waExecutions.length === 0 && <div className="text-muted-foreground">Sem execuções registradas.</div>}
        </div>
        <div className="flex justify-end pt-3">
          <Button variant="outline" onClick={() => setWaExecModal(false)}>Fechar</Button>
        </div>
      </Modal>

      {/* Modal - Estatísticas */}
      <Modal
        isOpen={waStatsModal}
        onClose={() => setWaStatsModal(false)}
        title="Estatísticas da Automação"
      >
        {waStats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Execuções</div>
              <div className="text-lg font-semibold text-foreground">{waStats.totalExecutions}</div>
            </div>
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Enviadas</div>
              <div className="text-lg font-semibold text-foreground">{waStats.messagesSent}</div>
            </div>
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Falhas</div>
              <div className="text-lg font-semibold text-foreground">{waStats.messagesFailed}</div>
            </div>
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-xs text-muted-foreground">Taxa de Sucesso</div>
              <div className="text-lg font-semibold text-foreground">{waStats.successRate}%</div>
            </div>
            <div className="p-3 bg-muted/50 rounded col-span-2 md:col-span-4">
              <div className="text-xs text-muted-foreground">Tempo Médio de Execução</div>
              <div className="text-lg font-semibold text-foreground">{waStats.averageExecutionTime} ms</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Sem dados.</div>
        )}
        <div className="flex justify-end pt-3">
          <Button variant="outline" onClick={() => setWaStatsModal(false)}>Fechar</Button>
        </div>
      </Modal>

      {/* Modal - Preview */}
      <Modal
        isOpen={waShowModal}
        onClose={() => { setWaShowModal(false); setWaSelected(null); }}
        title={waSelected ? 'Editar Automação WhatsApp' : 'Nova Automação WhatsApp'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
              <Input value={waForm.name} onChange={(e) => setWaForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Empresa</label>
              <Select value={waForm.companyId || ''} onValueChange={(v) => setWaForm(prev => ({ ...prev, companyId: v || undefined }))}>
                <option value="">(Opcional)</option>
                {waCompanies.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input value={waForm.description} onChange={(e) => setWaForm(prev => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tipo de Trigger</label>
              <Select value={waForm.triggerType} onValueChange={(v) => setWaForm(prev => ({ ...prev, triggerType: v }))}>
                <option value="">Selecione</option>
                <option value="days_before_due">Dias antes do vencimento</option>
                <option value="days_after_due">Dias após o vencimento</option>
                <option value="status_change">Mudança de status</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Valor do Trigger</label>
              <Input type="number" value={waForm.triggerValue} onChange={(e) => setWaForm(prev => ({ ...prev, triggerValue: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Template WhatsApp</label>
              <Select value={waForm.templateId || ''} onValueChange={(v) => { setWaForm(prev => ({ ...prev, templateId: v })); refreshWaPreviewVarsFromTemplate(v); }}>
                <option value="">(Opcional)</option>
                {waTemplates.map((t: any) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setWaShowModal(false); setWaSelected(null); }}>Cancelar</Button>
            <Button onClick={saveWaAutomation}>Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Modal - Template WhatsApp (CRUD + Preview/Teste) */}
      <Modal
        isOpen={waTemplateModal}
        onClose={() => setWaTemplateModal(false)}
        title={waTemplateSelected ? 'Editar Template WhatsApp' : 'Novo Template WhatsApp'}
      >
        <div className="space-y-3 text-sm">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Nome</label>
              <Input value={waTemplateForm.name} onChange={(e) => setWaTemplateForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Categoria</label>
              <Input value={waTemplateForm.category} onChange={(e) => setWaTemplateForm(prev => ({ ...prev, category: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Conteúdo</label>
            <textarea className="w-full p-2 border rounded-md" rows={4}
              value={waTemplateForm.content}
              onChange={(e) => setWaTemplateForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Mensagem com variáveis {{nome}}, {{valor}}, ..."
            />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={detectWaTemplateVariables}>Detectar Variáveis</Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Telefone para teste</label>
              <Input value={waTemplateTestPhone} onChange={(e) => setWaTemplateTestPhone(e.target.value)} placeholder="+5511999999999" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={testSendWaTemplate} disabled={!waTemplateSelected?.id || !waTemplateTestPhone.trim()}>Enviar Teste</Button>
              <Button onClick={saveWaTemplate} disabled={waTemplateSaving}>{waTemplateSaving ? 'Salvando...' : 'Salvar Template'}</Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Conteúdo da Aba Metas dos Operadores */}
      {activeTab === 'operador-metas' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Metas dos Operadores</h2>
            <Button onClick={() => setShowMetaModal(true)}>
              <Target className="w-4 h-4 mr-2" />
              Nova Meta
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de Metas */}
            <Card>
              <CardHeader>
                <CardTitle>Metas Configuradas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {operadorMetas.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhuma meta configurada</p>
                      <p className="text-sm">Clique em "Nova Meta" para começar</p>
                    </div>
                  ) : (
                    operadorMetas.map((meta) => (
                      <div key={meta.id} className="border rounded-lg p-4 hover:bg-muted/50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium">{meta.user?.name || 'Operador'}</h3>
                            <p className="text-sm text-muted-foreground">{meta.tipo}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedMeta(meta);
                                setMetaForm({
                                  userId: meta.userId,
                                  tipo: meta.tipo,
                                  titulos: meta.titulos,
                                  valorRecuperado: meta.valorRecuperado || 0,
                                  taxaConversao: meta.taxaConversao || 0,
                                  tempoMedioResolucao: meta.tempoMedioResolucao || 0,
                                });
                                setShowMetaModal(true);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMeta(meta.id)}
                            >
                              Excluir
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Títulos:</span>
                            <span className="ml-2 font-medium">{meta.titulos}</span>
                          </div>
                          {meta.valorRecuperado && (
                            <div>
                              <span className="text-muted-foreground">Valor:</span>
                              <span className="ml-2 font-medium">R$ {meta.valorRecuperado.toLocaleString()}</span>
                            </div>
                          )}
                          {meta.taxaConversao && (
                            <div>
                              <span className="text-muted-foreground">Taxa Conversão:</span>
                              <span className="ml-2 font-medium">{meta.taxaConversao}%</span>
                            </div>
                          )}
                          {meta.tempoMedioResolucao && (
                            <div>
                              <span className="text-muted-foreground">Tempo Médio:</span>
                              <span className="ml-2 font-medium">{meta.tempoMedioResolucao}h</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Estatísticas */}
            <Card>
              <CardHeader>
                <CardTitle>Estatísticas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/40 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{operadorMetas.length}</div>
                      <div className="text-sm text-muted-foreground">Metas Ativas</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {operadorMetas.filter(m => m.tipo === 'DIARIA').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Metas Diárias</div>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>• Configure metas personalizadas para cada operador</p>
                    <p>• Defina objetivos de títulos, valor recuperado e taxa de conversão</p>
                    <p>• Acompanhe o progresso em tempo real no Kanban</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Conteúdo da Aba Personalização */}
      {activeTab === 'personalizacao' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Personalização do Sistema</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configurações de Logo e Nome */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  Logo e Nome do Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome do Sistema
                  </label>
                  <Input
                    value={customization.systemName}
                    onChange={(e) => setCustomization(prev => ({ ...prev, systemName: e.target.value }))}
                    placeholder="Digite o nome do sistema"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Logo do Sistema
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                        ) : (
                          <Image className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-950/40 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Formatos aceitos: PNG, JPG, SVG. Máximo 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações */}
            <Card>
              <CardHeader>
                <CardTitle>Ações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex space-x-2">
                  <Button
                    onClick={handleSaveCustomization}
                    disabled={savingCustomization}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingCustomization ? 'Salvando...' : 'Salvar Personalização'}
                  </Button>
                  <Button
                    onClick={handleResetCustomization}
                    variant="outline"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resetar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Conteúdo da Aba API Externa */}
      {activeTab === 'external-api' && (
        <ExternalApiConfigTab />
      )}

      {activeTab === 'rique-atendimento' && <RiqueAtendimentoPromptTab />}

      {/* Conteúdo da Aba Notificações */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Sistema de Notificações</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Teste de Notificações */}
            <div>
              <NotificationTest />
            </div>

            {/* Informações do Sistema */}
            <Card>
              <CardHeader>
                <CardTitle>Informações do Sistema</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Funcionalidades Implementadas</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Notificações em tempo real via WebSocket
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Push notifications do navegador
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Notificações sonoras configuráveis
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Diferentes tipos e prioridades
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Histórico de notificações
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium mb-2">Como Usar</h3>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>1. Use o formulário ao lado para enviar notificações de teste</p>
                    <p>2. As notificações aparecerão no ícone do sino no header</p>
                    <p>3. Clique no sino para ver todas as notificações</p>
                    <p>4. Configure o som e push notifications conforme necessário</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Modal de Meta */}
      <Modal
        isOpen={showMetaModal}
        onClose={() => {
          setShowMetaModal(false);
          setSelectedMeta(null);
          setMetaForm({
            userId: '',
            tipo: 'DIARIA',
            titulos: 0,
            valorRecuperado: 0,
            taxaConversao: 0,
            tempoMedioResolucao: 0,
          });
        }}
        title={selectedMeta ? 'Editar Meta' : 'Nova Meta'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Operador
              </label>
              <Select
                value={metaForm.userId}
                onValueChange={(value) => setMetaForm(prev => ({ ...prev, userId: value }))}
                disabled={!!selectedMeta}
              >
                <option value="">Selecione um operador</option>
                {operadores.map((operador) => (
                  <option key={operador.id} value={operador.id}>
                    {operador.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tipo
              </label>
              <Select
                value={metaForm.tipo}
                onValueChange={(value) => setMetaForm(prev => ({ ...prev, tipo: value as 'DIARIA' | 'SEMANAL' | 'MENSAL' }))}
              >
                <option value="DIARIA">Diária</option>
                <option value="SEMANAL">Semanal</option>
                <option value="MENSAL">Mensal</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Títulos
              </label>
              <Input
                type="number"
                value={metaForm.titulos}
                onChange={(e) => setMetaForm(prev => ({ ...prev, titulos: parseInt(e.target.value) || 0 }))}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Valor Recuperado (R$)
              </label>
              <Input
                type="number"
                value={metaForm.valorRecuperado}
                onChange={(e) => setMetaForm(prev => ({ ...prev, valorRecuperado: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Taxa de Conversão (%)
              </label>
              <Input
                type="number"
                value={metaForm.taxaConversao}
                onChange={(e) => setMetaForm(prev => ({ ...prev, taxaConversao: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tempo Médio (horas)
              </label>
              <Input
                type="number"
                value={metaForm.tempoMedioResolucao}
                onChange={(e) => setMetaForm(prev => ({ ...prev, tempoMedioResolucao: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowMetaModal(false);
                setSelectedMeta(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveMeta}
              disabled={savingMeta || !metaForm.userId}
            >
              {savingMeta ? 'Salvando...' : (selectedMeta ? 'Atualizar' : 'Criar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Confirmação para Limpeza de Dados */}
      <Modal
        isOpen={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        title="Confirmar Limpeza de Dados"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  ⚠️ ATENÇÃO: Esta ação é irreversível!
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Você está prestes a remover permanentemente todos os dados da empresa selecionada.</p>
                  <p className="mt-2 font-medium">Esta ação não pode ser desfeita!</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Para confirmar, digite "LIMPAR" no campo abaixo:
            </label>
            <Input
              value={cleanupConfirmation}
              onChange={(e) => setCleanupConfirmation(e.target.value)}
              placeholder="Digite LIMPAR para confirmar"
              className="w-full"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCleanupModal(false);
                setCleanupConfirmation('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCleanupData}
              disabled={cleanupConfirmation !== 'LIMPAR' || cleanupLoading}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              {cleanupLoading ? 'Limpando...' : 'Confirmar Limpeza'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
